import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn, concat } from '@ember/helper';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  HdsTable,
  HdsIcon,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import { Flex, Time } from 'core/components';

/**
 * Component used to render a Project level service principal's workload identity providers
 *
 * ### Overview
 *
 * `ServicePrincipalWorkloadIdentityProvidersListTableComponent` renders an <Hds::Table /> of Workload identity providers for a project level service principal.
 *
 * Usage:
 * <ServicePrincipal::WorkloadIdentityProviders::ListTable
 *  @workloadIdentityProviders={{foo}}
 *  @organizationName={{foo}}
 *  @projectName={{foo}}
 *  @servicePrincipalName={{foo}}
 *  @handleConfirmDeleteWip={{fooFunction}}
 * />
 *
 *
 */
export default class ServicePrincipalWorkloadIdentityProvidersListTable extends Component {
  /**
   * @argument workloadIdentityProviders
   * @type {Array.<Object>}
   * @required
   */
  /**
   * @argument organzationName
   * @type {String}
   * @required
   */
  /**
   * @argument projectName
   * @type {String}
   * @required
   */
  /**
   * @argument servicePrincipalName
   * @type {String}
   * @required
   */
  /**
   * @argument handleConfirmDeleteWip
   * @type {Function}
   * @required
   */
  <template>
    <HdsTable
      @caption="HCP organization '{{@organizationName}}': list of Workload identity providers for service principal '{{@servicePrincipalName}}' in project '{{@projectName}}'"
      @valign="middle"
    >
      <:head as |H|>
        <H.Tr>
          <H.Th>
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.list-table.headers.name"
            }}
          </H.Th>
          <H.Th>
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.list-table.headers.description"
            }}
          </H.Th>
          <H.Th>
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.list-table.headers.provider"
            }}
          </H.Th>
          <H.Th>
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.list-table.headers.created"
            }}
          </H.Th>
          {{#if (can "update workload-identity-providers")}}
            <H.Th>
              {{t
                "components.page.access-control.service-principals.details.workload-identity-providers.list-table.headers.actions"
              }}
            </H.Th>
          {{/if}}
        </H.Tr>
      </:head>
      <:body as |B|>
        {{#each @workloadIdentityProviders as |wip index|}}
          <B.Tr data-test-service-principal-wip-list-table-item>
            <B.Td
              data-test-service-principal-wip-list-table-item-name
            >{{wip.displayName}}</B.Td>
            <B.Td
              data-test-service-principal-wip-list-table-item-description
            >{{wip.description}}</B.Td>
            <B.Td>
              <Flex @alignItems="center" @direction="row" @gap="2xs">
                {{#if wip.providerIconName}}
                  <HdsIcon
                    @name="{{wip.providerIconName}}-color"
                    @isInline={{true}}
                    data-test-service-principal-wip-list-table-item-provider-icon
                  />
                {{else}}
                  <HdsIcon
                    @name="openid-color"
                    @isInline={{true}}
                    data-test-service-principal-wip-list-table-item-provider-icon
                  />
                {{/if}}
                <span
                  data-test-service-principal-wip-list-table-item-provider-label
                >
                  {{#if wip.awsConfig}}
                    {{t
                      "components.page.access-control.service-principals.details.workload-identity-providers.list-table.provider-labels.aws"
                    }}
                  {{else if wip.oidcConfig}}
                    {{#if (eq wip.providerIconName "azure")}}
                      {{t
                        "components.page.access-control.service-principals.details.workload-identity-providers.list-table.provider-labels.azure"
                      }}
                    {{else if (eq wip.providerIconName "gcp")}}
                      {{t
                        "components.page.access-control.service-principals.details.workload-identity-providers.list-table.provider-labels.gcp"
                      }}
                    {{else if (eq wip.providerIconName "github")}}
                      {{t
                        "components.page.access-control.service-principals.details.workload-identity-providers.list-table.provider-labels.github"
                      }}
                    {{else if (eq wip.providerIconName "gitlab")}}
                      {{t
                        "components.page.access-control.service-principals.details.workload-identity-providers.list-table.provider-labels.gitlab"
                      }}
                    {{else}}
                      {{t
                        "components.page.access-control.service-principals.details.workload-identity-providers.list-table.provider-labels.oidc"
                      }}
                    {{/if}}
                  {{else}}
                  {{/if}}
                </span>
              </Flex>
            </B.Td>

            <B.Td data-test-service-principal-wip-list-table-item-created-at>
              <Time @date={{wip.createdAt}} @display="relative" />
            </B.Td>

            {{#if (can "update workload-identity-providers")}}
              <B.Td>
                <HdsDropdown as |dd|>
                  <dd.ToggleIcon
                    @icon="more-horizontal"
                    @text="Overflow Options"
                    @hasChevron={{false}}
                    data-test-service-principal-wip-list-table-item-menu-toggle
                  />
                  {{#if (can "delete workload-identity-providers")}}
                    <dd.Interactive
                      @text={{t
                        "components.page.access-control.service-principals.details.workload-identity-providers.list-table.actions.menu.delete"
                      }}
                      @color="critical"
                      @icon="trash"
                      id="modal-dialog-delete-open-button-{{index}}"
                      {{on
                        "click"
                        (fn
                          @handleConfirmDeleteWip
                          wip
                          (concat "modal-dialog-delete-open-button-" index)
                        )
                      }}
                      data-test-service-principal-wip-list-table-item-menu-delete
                    />
                  {{/if}}
                </HdsDropdown>
              </B.Td>
            {{/if}}
          </B.Tr>
        {{/each}}
      </:body>
    </HdsTable>
  </template>
}
